import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  semanticId?: string;
  error?: boolean;
  valid?: boolean;
}

const transientProps = ['$error', '$valid'] as const;

const shouldForwardTransient = (prop: string | number): boolean =>
  !transientProps.includes(prop as (typeof transientProps)[number]);

const StyledInput = styled.input.withConfig({ shouldForwardProp: shouldForwardTransient })<{
  $error: boolean;
  $valid: boolean;
}>`
  background-color: var(--ui-surface);
  border: ${rem(1)} solid var(--ui-input-border, var(--ui-border));
  border-radius: var(--ui-radius-md, ${rem(6)});
  padding: ${rem(11)} ${rem(12)};
  -webkit-appearance: none;

  font-weight: 400;
  font-size: ${rem(15)};
  line-height: ${rem(16)};
  font-family: var(--ui-font-sans);

  filter: drop-shadow(${rem(0)} ${rem(2)} ${rem(4)} var(--ui-input-shadow, var(--ui-shadow-color)));
  transition: all 0.2s 0s ease;
  min-width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;

  &:hover,
  &:focus {
    border: ${rem(1)} solid var(--ui-input-border-focus, var(--ui-interactive));
    outline: none;
  }

  ${({ $error }) =>
    $error &&
    css`
      &:not(:disabled) {
        border: ${rem(1)} solid var(--ui-status-danger);
      }
    `}

  ${({ $valid }) =>
    $valid &&
    css`
      &:not(:disabled) {
        border: ${rem(1)} solid var(--ui-status-success);
      }
    `}

  &:disabled {
    background-color: var(--ui-background-muted);
    border: ${rem(1)} solid var(--ui-border);
  }

  &::placeholder {
    font-weight: 400;
    color: var(--ui-text-subtle);
  }

  &:placeholder-shown {
    text-overflow: ellipsis;
  }
`;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { semanticId, error = false, valid = false, disabled, type = 'text', ...rest },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <StyledInput
      ref={ref}
      type={type}
      $error={error}
      $valid={valid}
      disabled={disabled}
      aria-invalid={error || undefined}
      data-error={error || undefined}
      data-valid={valid || undefined}
      data-disabled={disabled || undefined}
      {...semanticProps}
      {...rest}
    />
  );
});

TextInput.displayName = 'TextInput';
